import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { ConfirmationService } from "primeng/api";
import { Editor } from "primeng/editor";
import { InputSwitch } from "primeng/inputswitch";
import Quill, { DeltaOperation } from "quill";
import { AppStateService } from "../../core/services/app-state.service";
import { EditorType } from "./text-editor.model";

@Component({
	selector: 'lb-text-editor',
	templateUrl: './text-editor.component.html',
	styleUrls: ['./text-editor.component.css']
})
export class TextEditorComponent implements OnInit {
	@ViewChild('pEditor') editor?: Editor;
	
	@Input() height?: number;
	@Input() model = '';
	@Input() showRichTextToggle = true;
	@Input() useAcceptRejectButtons = false;
	@Input() name?: string
	@Input() placeholder = 'Type and style text here.'

	@Input()
	get useRichText() {
		return this._useRichText;
	}
	set useRichText(useRichText) {
		this._useRichText = useRichText;

		this.setEditorType();
	}
	
	@Input()
	set width(width: number | undefined) {
		if (width)
			this.editorWidth = width + 'rem';
	}

	@Output() modelChanged = new EventEmitter<{ text: string, useRichText: boolean }>();
	@Output() acceptClicked = new EventEmitter<{ text: string, useRichText: boolean }>();
	@Output() rejectClicked = new EventEmitter();

	@ViewChild(InputSwitch) switch!: InputSwitch;

	editorHeight!: string;
	editorWidth = '100%';
	editorType!: EditorType;
	guid = new Date().getTime().toString();
	modifierKey = AppStateService.modifierKey;
	quillModules = {
		imageDropAndPaste: {}
	};

	private quillEditor!: any;
	private _useRichText = false;
	private richTextEnabledLocally = false; // Could also be enabled via parent input binding (e.g. - from a note sync)

	constructor(
		private confirmationService: ConfirmationService,
		public elRef: ElementRef
	) { }
	
	ngOnInit() {
		const lineHeight = 1.42,
			verticalPadding = '24px',
			numberOfLines = this.height || 2;
		
		this.setEditorType();
		this.editorHeight = `calc(${numberOfLines * lineHeight}rem + ${verticalPadding}`;
	}

	focus() {
		let el: HTMLElement;

		switch (this.editorType) {
			case "Rich":
				el = this.elRef.nativeElement.getElementsByClassName('ql-editor').item(0);
				break;
			case "TextInput":
				el = this.elRef.nativeElement.getElementsByClassName('text-input-entry').item(0);
				break;
			case "TextArea":
				el = this.elRef.nativeElement.getElementsByClassName('text-area-entry').item(0);
				break;
		}
		
		el.focus();
	}

	onQuillInit(quillEditor: any) {
		this.quillEditor = quillEditor;

		// Replace plaintext with HTML when toggling to a rich text note
		if (this.richTextEnabledLocally) {
			quillEditor.setText(this.model);
			this.model = quillEditor.root.innerHTML;

			this.richTextEnabledLocally = false;
			this.onModelChanged();
		}

		this.createDynamicElementTooltips();
		this.overwriteEditorWriteMethod();
	}

	onModelChanged() {
		this.modelChanged.emit({
			text: this.model,
			useRichText: this.useRichText
		});
	}

	onAcceptClicked() {
		this.acceptClicked.emit({
			text: this.model.trim(),
			useRichText: this.useRichText
		});
	}

	onRejectClicked() {
		this.rejectClicked.emit();
	}

	onUseRichTextToggled(e: { checked: boolean, originalEvent: Event }) {
		if (e.checked) {
			this.richTextEnabledLocally = true;
			this.useRichText = true;
		}
		else {
			const usePlainText = () => {
				this.useRichText = false;
				this.model = this.quillEditor.getText().trim();

				this.onModelChanged();
			};
	
			const hasFormatting = this.quillEditor.getContents().ops.some((o: DeltaOperation) =>
				o.attributes || typeof o.insert == 'object'
			);
	
			if (hasFormatting) {
				this.confirmationService.confirm({
					key: this.guid,
					message: 'Switch to a regular text editor and lose all rich formatting?',
					accept: usePlainText,
					reject: () => this.switch.toggle(e.originalEvent)
				});
			}
			else
				usePlainText();
		}
	}

	private setEditorType() {
		this.editorType =
			this.useRichText ? 'Rich' :
			this.height ? 'TextArea' : 
			'TextInput';
	}
	
	/**
	 * Overwrite the `writeValue` method of the P-Editor to capture the before cursor position and place it
	 * back once the quill editor has its value updated.
	 */
	private overwriteEditorWriteMethod() {
		setTimeout(() => {
			if (this.editor) {
				const editor = this.editor;
				const original = editor.writeValue.bind(this.editor);
				editor.writeValue = function(this: typeof editor, ...params: Parameters<Editor['writeValue']>) {
					const quill = this.quill as Quill;
					const selection = quill.getSelection();
					original(...params);
					
					if (selection) {
						quill.setSelection(selection);
					}
				}
			}
		});
	}

	private createDynamicElementTooltips() {
		const tooltips = ({
			font: 'Font Style',
			size: 'Font Size',
			align: 'Alignment',
			color: 'Text Color',
			background: 'Background Color'
		}),
		toolbar = this.quillEditor.container.previousElementSibling as HTMLElement;

		for (const [key, value] of Object.entries(tooltips))
			toolbar.querySelector(`.ql-${key} > .ql-picker-label`)!.setAttribute('title', value);
	}
}